// Operator runbook viewer. Renders OPERATOR_RUNBOOK.md (bundled at build
// time via Vite's ?raw import) as a self-contained reading view using
// a focused, dependency-free markdown parser tailored to the runbook's
// content.
//
// The parser handles exactly the markdown features the runbook uses:
//   - ATX headings (# … ######)
//   - Paragraphs (blank-line separated)
//   - Bold (**), italic (*), inline code (`)
//   - Unordered (- ) and ordered (1. ) lists
//   - Blockquotes (> )
//   - Fenced code blocks (```)
//   - GFM pipe tables (| col | col |)
//   - Horizontal rules (---)
//   - Links ([text](url)) and bare URLs (https://...)
//
// It is intentionally not a general-purpose markdown parser — features
// the runbook does not use (nested lists, images, HTML) are not
// supported. Adding a full markdown lib for one static file would add
// ~30 KB to the bundle for no benefit.

import React, { useMemo, useState } from 'react';
// @ts-ignore
import runbookSource from '../../OPERATOR_RUNBOOK.md?raw';

const HEADING_TAGS = { 1: 'h1', 2: 'h2', 3: 'h3', 4: 'h4', 5: 'h5', 6: 'h6' };

function parseMarkdown(src) {
  const lines = src.split(/\r?\n/);
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    if (/^\s*([-*_])\s*\1\s*\1[\s\1]*$/.test(line)) {
      blocks.push({ kind: 'hr' });
      i++;
      continue;
    }

    const fence = line.match(/^```(\S*)\s*$/);
    if (fence) {
      const lang = fence[1] || '';
      const buf = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++;
      blocks.push({ kind: 'code', lang, text: buf.join('\n') });
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      blocks.push({ kind: 'heading', level, text: heading[2].trim() });
      i++;
      continue;
    }

    if (/^\s*>/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) {
        buf.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      blocks.push({ kind: 'quote', text: buf.join('\n') });
      continue;
    }

    if (/^\s*\|/.test(line) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const header = splitRow(lines[i]);
      i += 2;
      const rows = [];
      while (i < lines.length && /^\s*\|/.test(lines[i])) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ kind: 'table', header, rows });
      continue;
    }

    if (/^\s*([-*+])\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
      const ordered = /^\s*\d+\.\s+/.test(line);
      const items = [];
      const re = ordered ? /^\s*\d+\.\s+(.*)$/ : /^\s*[-*+]\s+(.*)$/;
      while (i < lines.length) {
        const m = lines[i].match(re);
        if (!m) break;
        items.push(m[1]);
        i++;
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    const buf = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#{1,6}\s|```|>\s|\s*[-*+]\s|\s*\d+\.\s|\s*\|)/.test(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    blocks.push({ kind: 'paragraph', text: buf.join(' ') });
  }
  return blocks;
}

function splitRow(line) {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(s => s.trim());
}

function renderInline(text, keyPrefix) {
  const out = [];
  let buf = '';
  let i = 0;
  let key = 0;
  const flush = () => {
    if (buf) { out.push(buf); buf = ''; }
  };
  while (i < text.length) {
    const ch = text[i];

    if (ch === '`') {
      const end = text.indexOf('`', i + 1);
      if (end !== -1) {
        flush();
        out.push(<code key={`${keyPrefix}-c-${key++}`}>{text.slice(i + 1, end)}</code>);
        i = end + 1;
        continue;
      }
    }

    if (ch === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        flush();
        out.push(<strong key={`${keyPrefix}-b-${key++}`}>{renderInline(text.slice(i + 2, end), `${keyPrefix}-bi-${key}`)}</strong>);
        i = end + 2;
        continue;
      }
    }

    if (ch === '*' && text[i + 1] !== '*') {
      const end = text.indexOf('*', i + 1);
      if (end !== -1 && end > i + 1) {
        flush();
        out.push(<em key={`${keyPrefix}-i-${key++}`}>{renderInline(text.slice(i + 1, end), `${keyPrefix}-ii-${key}`)}</em>);
        i = end + 1;
        continue;
      }
    }

    if (ch === '[') {
      const close = text.indexOf(']', i + 1);
      if (close !== -1 && text[close + 1] === '(') {
        const urlEnd = text.indexOf(')', close + 2);
        if (urlEnd !== -1) {
          flush();
          const linkText = text.slice(i + 1, close);
          const url = text.slice(close + 2, urlEnd);
          out.push(<a key={`${keyPrefix}-a-${key++}`} href={url} target="_blank" rel="noopener noreferrer">{renderInline(linkText, `${keyPrefix}-ai-${key}`)}</a>);
          i = urlEnd + 1;
          continue;
        }
      }
    }

    if ((ch === 'h' || ch === 'H') && (text.slice(i, i + 8) === 'https://' || text.slice(i, i + 7) === 'http://')) {
      const protoLen = text.slice(i, i + 8) === 'https://' ? 8 : 7;
      const rest = text.slice(i + protoLen);
      const m = rest.match(/^[^\s<>()]+/);
      if (m) {
        flush();
        const url = text.slice(i, i + protoLen + m[0].length);
        out.push(<a key={`${keyPrefix}-u-${key++}`} href={url} target="_blank" rel="noopener noreferrer">{url}</a>);
        i += protoLen + m[0].length;
        continue;
      }
    }

    buf += ch;
    i++;
  }
  flush();
  return out;
}

function renderBlock(block, idx) {
  const key = `b-${idx}`;
  switch (block.kind) {
    case 'heading': {
      const Tag = HEADING_TAGS[block.level] || 'h2';
      return <Tag key={key} className={`md-heading md-h${block.level}`}>{renderInline(block.text, `h-${idx}`)}</Tag>;
    }
    case 'paragraph':
      return <p key={key} className="md-p">{renderInline(block.text, `p-${idx}`)}</p>;
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return <Tag key={key} className="md-list">{block.items.map((it, j) => <li key={`${key}-${j}`}>{renderInline(it, `li-${idx}-${j}`)}</li>)}</Tag>;
    }
    case 'quote':
      return <blockquote key={key} className="md-quote">{renderInline(block.text, `q-${idx}`)}</blockquote>;
    case 'code':
      return <pre key={key} className="md-code"><code>{block.text}</code></pre>;
    case 'hr':
      return <hr key={key} className="md-hr" />;
    case 'table':
      return (
        <div key={key} className="md-table-wrap">
          <table className="md-table">
            <thead>
              <tr>{block.header.map((c, j) => <th key={`h-${j}`}>{renderInline(c, `th-${idx}-${j}`)}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => <tr key={`r-${r}`}>{row.map((c, j) => <td key={`c-${j}`}>{renderInline(c, `td-${idx}-${r}-${j}`)}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

export default function Runbook() {
  const blocks = useMemo(() => parseMarkdown(runbookSource), []);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return blocks;
    const result = [];
    let lastHeading = null;
    let headingKept = false;
    for (const b of blocks) {
      const hay = (() => {
        switch (b.kind) {
          case 'heading': return b.text;
          case 'paragraph': return b.text;
          case 'list': return b.items.join(' ');
          case 'quote': return b.text;
          case 'code': return b.text;
          case 'table': return b.header.join(' ') + ' ' + b.rows.map(r => r.join(' ')).join(' ');
          case 'hr': return '';
          default: return '';
        }
      })().toLowerCase();
      const matches = hay.includes(q);
      if (b.kind === 'heading') {
        if (matches) {
          result.push(b);
          lastHeading = b;
          headingKept = true;
        } else {
          lastHeading = b;
          headingKept = false;
        }
      } else if (matches) {
        if (lastHeading && !headingKept) {
          result.push(lastHeading);
          headingKept = true;
        }
        result.push(b);
      }
    }
    return result;
  }, [blocks, query]);

  return (
    <div className="runbook">
      <div className="runbook-toolbar">
        <input
          type="search"
          className="runbook-search"
          placeholder="Filter sections (matches headings, paragraphs, lists, code, tables)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className="muted" style={{ fontSize: 12 }}>
          {query ? `${filtered.length} of ${blocks.length} blocks` : `${blocks.length} blocks`}
        </span>
      </div>
      <article className="runbook-content">
        {filtered.length === 0 ? (
          <p className="muted">No matches for &ldquo;{query}&rdquo;.</p>
        ) : (
          filtered.map((b, i) => renderBlock(b, i))
        )}
      </article>
    </div>
  );
}

